import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateOneTimeEntry } from "../../hooks/realestate";
import type { OneTimeEntry, UpdateOneTimeEntryInput } from "../../lib/realestate";
import { OneTimeEntryFormModal } from "./OneTimeEntryFormModal";

interface Props {
  propertyId: string;
  entries: OneTimeEntry[];
  onDelete: (entryId: string) => void;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function OneTimeEntryList({ propertyId, entries, onDelete }: Props) {
  const { t } = useTranslation();
  const updateMutation = useUpdateOneTimeEntry();
  const [editingEntry, setEditingEntry] = useState<OneTimeEntry | null>(null);

  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">{t("realestate.one_time.empty")}</p>;
  }
  return (
    <>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b">
            <th className="p-2">{t("realestate.one_time.date")}</th>
            <th className="p-2">{t("realestate.one_time.category")}</th>
            <th className="p-2">{t("realestate.one_time.description")}</th>
            <th className="p-2 text-right">{t("realestate.one_time.amount")}</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const isIncome = e.amountCents > 0;
            return (
              <tr key={e.id} className="border-b">
                <td className="p-2 font-mono text-xs">{e.date}</td>
                <td className="p-2">
                  <span className="px-2 py-0.5 text-xs bg-slate-100 rounded">
                    {t(`realestate.one_time.category_label.${e.category}`)}
                  </span>
                </td>
                <td className="p-2">{e.description}</td>
                <td
                  className={`p-2 text-right font-mono ${isIncome ? "text-green-700" : "text-red-700"}`}
                >
                  {formatEur(e.amountCents)}
                </td>
                <td className="p-2 text-right">
                  <button
                    type="button"
                    className="text-xs text-blue-600 underline mr-2"
                    onClick={() => setEditingEntry(e)}
                  >
                    {t("realestate.one_time.edit_button")}
                  </button>
                  <button
                    type="button"
                    className="text-red-700 text-xs"
                    onClick={() => onDelete(e.id)}
                  >
                    {t("common.delete")}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editingEntry && (
        <OneTimeEntryFormModal
          open={editingEntry !== null}
          existing={editingEntry}
          submitting={updateMutation.isPending}
          onClose={() => setEditingEntry(null)}
          onSubmit={async (input) => {
            await updateMutation.mutateAsync({
              propertyId,
              entryId: editingEntry.id,
              input: input as UpdateOneTimeEntryInput,
            });
            setEditingEntry(null);
          }}
        />
      )}
    </>
  );
}
